/**
 * IncomingCallModal - the ring. A small card in the top-right corner with Join and Decline.
 *
 * Why the geometry is written twice - here as inline styles AND in VideoCall.css.
 *
 * This modal was reported as taking over the whole screen on an incoming call, which the
 * stylesheet does not explain: `.incoming-call-modal` has been `position: fixed; top: 20px;
 * right: 20px` with a 400px-max card in every commit of this file's history. What it does
 * explain is what happens when that stylesheet is NOT applied to this markup, which was
 * measured in a browser: the modal loses `position: fixed` and becomes a static, full-width
 * block, and - the part that actually fills the screen - the avatar's `<svg viewBox="0 0 24
 * 24">` carried no width or height of its own. An SVG with no intrinsic size and no CSS size
 * defaults to `width: auto` = 100% of its container and takes its height from the viewBox
 * ratio, so at a 1280px-wide viewport it renders 1280x1280: a full-screen person icon with
 * "Incoming Video Call" under it. That is exactly the report.
 *
 * The stylesheet can be missing here in a way it cannot be for the rest of the call UI.
 * VideoCall.css arrives as a side-effect import of this component, and this component is the
 * only part of the call stack mounted at the app root (main.tsx, GlobalCallUi) - everything
 * else is reached through ChatApp, which is lazy-loaded. Which bundle the stylesheet lands in
 * is therefore a code-splitting outcome, not something this component controls: put it in the
 * lazy ChatApp chunk and the ring renders unstyled for any engineer who has never opened the
 * chat panel, which is most of them, most of the time.
 *
 * So the ring does not ask to be styled to be small. Size, position and stacking are inline,
 * every icon carries explicit width/height attributes, and VideoCall.css only decorates. The
 * colours stay in the stylesheet's theme variables (inline `var(--call-*)` resolves from
 * :root), so a missing stylesheet costs the card its colours - never its size.
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVideoCall } from './VideoCallProvider';
import './VideoCall.css';

// Critical geometry: everything that decides how much of the screen this covers.
// Deliberately not in the stylesheet - see the note above.
const CRITICAL = {
    modal: {
        position: 'fixed',
        top: '20px',
        right: '20px',
        left: 'auto',
        bottom: 'auto',
        // Above the call stage, and above the legacy visualisation modals (RunbookModal.css
        // and RichTextEditModal.css go up to 10001, some of it !important) - a ring nobody
        // can see is a call nobody answers.
        zIndex: 100001,
        width: 'min(400px, calc(100vw - 40px))',
        maxHeight: 'calc(100vh - 40px)',
        boxSizing: 'border-box'
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        boxSizing: 'border-box'
    },
    avatar: { width: '40px', height: '40px', flexShrink: 0 },
    info: { flex: '1 1 auto', minWidth: 0 },
    actions: { display: 'flex', gap: '8px', flexShrink: 0 },
    // The announcement is inline-hidden for the same reason everything else here is inline:
    // unstyled, a screen-reader-only region is just a line of text in the middle of whatever
    // page the engineer is on.
    srOnly: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        padding: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
        border: 0
    }
};

function IncomingCallModal({ onAccept, onReject }) {
    const { t } = useTranslation();
    const {
        isIncoming,
        isInCall,
        callId,
        sessionId,
        callType,
        initiatorName,
        joinCall,
        rejectCall
    } = useVideoCall();

    const joinButtonRef = useRef(null);

    // Play ringtone when incoming call
    useEffect(() => {
        if (isIncoming) {
            // Create simple ringtone using Web Audio API
            try {
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                // The ring can now arrive on a page the engineer has not clicked on, and an
                // AudioContext created without a user gesture starts suspended - a silent
                // ringtone with no error. Ask for it back; the modal is visible either way.
                if (audioContext.state === 'suspended') {
                    audioContext.resume().catch(() => {});
                }
                let oscillator = null;
                let gainNode = null;
                let isPlaying = true;

                const playRingtone = () => {
                    if (!isPlaying) return;

                    oscillator = audioContext.createOscillator();
                    gainNode = audioContext.createGain();

                    oscillator.connect(gainNode);
                    gainNode.connect(audioContext.destination);

                    oscillator.frequency.value = 440;
                    oscillator.type = 'sine';

                    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

                    oscillator.start(audioContext.currentTime);
                    oscillator.stop(audioContext.currentTime + 0.5);

                    // Play second tone
                    setTimeout(() => {
                        if (!isPlaying) return;
                        const osc2 = audioContext.createOscillator();
                        const gain2 = audioContext.createGain();
                        osc2.connect(gain2);
                        gain2.connect(audioContext.destination);
                        osc2.frequency.value = 480;
                        osc2.type = 'sine';
                        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
                        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
                        osc2.start(audioContext.currentTime);
                        osc2.stop(audioContext.currentTime + 0.5);
                    }, 600);
                };

                // Play ringtone pattern
                playRingtone();
                const interval = setInterval(playRingtone, 2000);

                return () => {
                    isPlaying = false;
                    clearInterval(interval);
                    audioContext.close();
                };
            } catch (e) {
                console.warn('Could not play ringtone:', e);
            }
        }
    }, [isIncoming]);

    // Handle accept.
    //
    // joinCall is invoked here, synchronously, as the first thing this click does: it asks
    // for full screen before its own first `await`, and that request is only granted while
    // the click still counts as user activation. Nothing awaitable may be added above it.
    const handleAccept = async () => {
        try {
            await joinCall(callId, sessionId);
            onAccept?.();
        } catch (error) {
            console.error('Error accepting call:', error);
        }
    };

    // Handle reject
    const handleReject = async () => {
        await rejectCall(callId, sessionId);
        onReject?.();
    };

    const ringing = isIncoming && !isInCall;

    // Reaching the ring from the keyboard, WITHOUT taking the keyboard away.
    //
    // This used to call `joinButtonRef.current.focus()` the moment a call arrived, which
    // contradicted the `aria-modal="false"` two elements below - that attribute is there
    // precisely so an incoming call does not trap the engineer out of the console they are
    // reading, and then focus was yanked out of it anyway. An engineer mid-sentence in a
    // runbook loses their cursor, the rest of what they type goes nowhere, and the next Space
    // or Enter answers a call they had not decided to take. On a recovery bridge that is an
    // accidental-answer hazard, not a convenience.
    //
    // So the ring never moves focus on its own. What replaces it:
    //
    //  - It is announced instead. The live region below is mounted whether or not a call is
    //    ringing (a live region added to the page at the same time as its content is not
    //    reliably announced), so the ring reaches a screen-reader user without stealing
    //    anything from a sighted one.
    //  - It is the last thing in the document - main.tsx mounts GlobalCallUi after the whole
    //    router - so it is the end of the tab order rather than buried in the middle of it.
    //  - Alt+Shift+A moves focus TO the Join button on demand. Moving focus because the
    //    engineer pressed a chord is not theft; `code` rather than `key` because Alt rewrites
    //    `key` on several layouts (macOS Alt+A is 'å'), and Alt+Shift+A is not claimed by the
    //    browsers or by any text field.
    //
    // Once focus is on the ring, Enter answers and Escape declines (see onRingKeyDown).
    useEffect(() => {
        if (!ringing) return;

        const onKeyDown = (event) => {
            if (event.code !== 'KeyA') return;
            if (!event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey) return;
            event.preventDefault();
            joinButtonRef.current?.focus();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [ringing]);

    // Escape declines - but only an Escape aimed at the ring.
    //
    // This was a `document` listener held for the whole time the phone was ringing, calling
    // preventDefault and not stopPropagation, so an Escape meant for a dialog, a dropdown or
    // a device picker underneath silently declined the call instead of closing what the
    // engineer was looking at, and the only trace was a 'Call declined' banner. Bound on the
    // card, it can only fire when focus is inside the card; stopPropagation as well, so it
    // does not carry on to anything listening above it either.
    const onRingKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        handleReject();
    };

    // Announced without being focused. Rendered even when nothing is ringing so the region
    // exists before its content does; empty the rest of the time.
    const announcement = ringing
        ? t('aiChat.calls.ringAnnouncement', {
            name: initiatorName || t('aiChat.calls.unknownCaller'),
            type: callType === 'video'
                ? t('aiChat.calls.incomingVideoCall')
                : t('aiChat.calls.incomingAudioCall')
        })
        : '';

    const liveRegion = (
        <div
            className="call-sr-only"
            style={CRITICAL.srOnly}
            role="status"
            aria-live="assertive"
            aria-atomic="true"
        >
            {announcement}
        </div>
    );

    // Don't show incoming call notification if we're already in a call
    // (handles race condition where socket event arrives before state update)
    if (!ringing) {
        return liveRegion;
    }

    return (
        <>
        {liveRegion}
        <div
            className="incoming-call-modal"
            style={CRITICAL.modal}
            role="dialog"
            // Not modal: an incoming call must not trap the engineer out of the console they
            // are in the middle of reading, which is often why they are being called. Nothing
            // here focuses itself either - see the keyboard note above.
            aria-modal="false"
            aria-labelledby="incoming-call-caller"
            onKeyDown={onRingKeyDown}
        >
            <div className="incoming-call-content" style={CRITICAL.content}>
                {/* Caller avatar with animation */}
                <div className="incoming-call-avatar" style={CRITICAL.avatar}>
                    <div className="avatar-ring ring-1" />
                    <div className="avatar-ring ring-2" />
                    <div className="avatar-circle">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                    </div>
                </div>

                {/* Call info */}
                <div className="incoming-call-info" style={CRITICAL.info}>
                    <h3 className="caller-name" id="incoming-call-caller">
                        {initiatorName || t('aiChat.calls.unknownCaller')}
                    </h3>
                    <p className="call-type-label">
                        {callType === 'video' ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M23 7l-7 5 7 5V7z" />
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                </svg>
                                {t('aiChat.calls.incomingVideoCall')}
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                {t('aiChat.calls.incomingAudioCall')}
                            </>
                        )}
                    </p>
                    {/* The shortcut has to be visible somewhere or it does not exist. It is
                        what replaces the focus this card no longer steals, so it lives on the
                        card rather than in a help page nobody opens while being paged. */}
                    <p className="ring-keyboard-hint">{t('aiChat.calls.ringKeyboardHint')}</p>
                </div>

                {/* Action buttons. Labelled, not icon-only: "which green circle is answer"
                    is not a question to ask someone who has just been paged. */}
                <div className="incoming-call-actions" style={CRITICAL.actions}>
                    <button
                        type="button"
                        className="call-action-btn reject-btn"
                        onClick={handleReject}
                        title={t('aiChat.calls.decline')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                        <span className="call-action-label">{t('aiChat.calls.decline')}</span>
                    </button>

                    <button
                        type="button"
                        ref={joinButtonRef}
                        className="call-action-btn accept-btn"
                        onClick={handleAccept}
                        title={t('aiChat.calls.accept')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        <span className="call-action-label">{t('aiChat.calls.join')}</span>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
}

export default IncomingCallModal;
